"use client";

import { useUserContext } from "@contexts/UserContext/index.context";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import FilterListIcon from "@mui/icons-material/FilterList";
import MapIcon from "@mui/icons-material/Map";
import {
  Badge,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Modal,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import type { ColumnFiltersState, PaginationState, SortingState } from "@tanstack/react-table";
import type { GetAllInput, OrderType } from "@types_def/common/filter.types";
import type { Delivery } from "@types_def/models/delivery.types";
import { UserRole } from "@types_def/models/user.type";
import { deliveryStatusRoutes } from "@utils/constants/DeliveryStatus/deliveryStatusRoutes";
import { useEffect, useState } from "react";
import { Link as RouterLink, useParams, useSearchParams } from "react-router-dom";
import getAllFiltered from "@/api/graphql/generic/get-all-filtered";
import ExportFilterForm from "@/components/export-filter.form";
import { DataTable } from "@/components/ui/data-table";
import { DataTableSelectionBar } from "@/components/ui/data-table-selection-bar";
import { DataTableToolbar } from "@/components/ui/data-table-toolbar";
import { deliveriesTableQueryRoot } from "@/features/delivery/deliveries-table-cache";
import { useTableState } from "@/hooks/delivery-filters";
import { useDataTable } from "@/hooks/use-data-table";
import { RoutePaths } from "@/utils/constants/routes";
import { toDeliveryListSortField } from "@/utils/delivery-list-sort-field";

import {
  DeliveryTableBulkActions,
  deliveryTableHasBulkSelectionActions,
} from "./bulk-selection-actions/delivery-table-bulk-actions";
import { getDeliveryTableColumnsByRole } from "./columns";
import DeliveryTableFilters from "./Filters";

type Params = {
  status: keyof typeof deliveryStatusRoutes;
};

export function DeliveriesTable() {
  "use no memo";
  const theme = useTheme();
  const { userState } = useUserContext();
  const userRole = userState?.["custom:role"] as UserRole;

  const { status: statusRoute } = useParams<Params>();
  const statusKey = deliveryStatusRoutes[statusRoute ?? "confirmed"];

  const canExport =
    (userRole === UserRole.ADMIN || userRole === UserRole.CLIENT) && statusRoute === "completed";

  const urlRole = userRole === UserRole.ADMIN ? "admin" : "client";

  const [searchParams, setSearchParams] = useSearchParams();
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);

  const { tableState } = useTableState(statusKey.key, urlRole);

  const page = Number.parseInt(searchParams.get("page") ?? "0", 10) || 0;
  const orderByRaw = searchParams.get("orderBy");
  const orderBy =
    orderByRaw !== null && orderByRaw !== ""
      ? (toDeliveryListSortField(orderByRaw) as keyof Delivery)
      : "id";
  const order = searchParams.get("order") === "asc" ? "asc" : "desc";

  const deliveryIdSearch = searchParams.get("deliveryId") ?? "";
  const vinSearch = searchParams.get("vin") ?? "";
  const vehicleCodeSearch = searchParams.get("vehicleCode") ?? "";

  const pageSize = Number.parseInt(searchParams.get("pageSize") ?? "10", 10) || 10;

  const queryInput: GetAllInput<Delivery, keyof Delivery> = {
    keyword: "",
    pagination: { page, perPage: pageSize },
    sort: { field: orderBy, order: order as OrderType },
    filter: tableState.filter,
    include: tableState.include,
  };

  const filterKey = JSON.stringify(tableState.filter);
  const includeKey = JSON.stringify(tableState.include);

  const queryKey = [
    ...deliveriesTableQueryRoot,
    statusKey.key,
    urlRole,
    page,
    pageSize,
    orderBy,
    order,
    filterKey,
    includeKey,
  ] as const;

  const enableRowSelection =
    userRole === UserRole.ADMIN && deliveryTableHasBulkSelectionActions(statusRoute);

  const currentRoute = statusRoute ?? "confirmed";
  const columns = getDeliveryTableColumnsByRole(userRole, currentRoute);
  const mapRoute =
    userRole === UserRole.ADMIN
      ? RoutePaths.adminPilotDeliveryOngoingMap
      : RoutePaths.clientPilotDeliveryOngoingMap;
  const showMapButton = currentRoute === "ongoing";

  const { data, isPending, isError } = useQuery({
    queryKey,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const response = await getAllFiltered<Delivery, keyof Delivery>(
        queryInput,
        "Delivery",
        tableState.deliveriesTableQueryFields
      );
      return response.data;
    },
    enabled: Boolean(tableState.deliveriesTableQueryFields),
  });

  const pageCount = Math.ceil((data?.total ?? 0) / pageSize);

  const externalColumnFilters: ColumnFiltersState = [];
  if (deliveryIdSearch) {
    externalColumnFilters.push({ id: "deliveryId", value: deliveryIdSearch });
  }
  if (vinSearch) {
    externalColumnFilters.push({ id: "vin", value: vinSearch });
  }
  if (vehicleCodeSearch) {
    externalColumnFilters.push({ id: "vehicleCode", value: vehicleCodeSearch });
  }

  const handlePaginationChange = (pagination: PaginationState) => {
    const next = new URLSearchParams(searchParams);
    pagination.pageIndex > 0 ? next.set("page", `${pagination.pageIndex}`) : next.delete("page");
    pagination.pageSize !== 10
      ? next.set("pageSize", String(pagination.pageSize))
      : next.delete("pageSize");
    setSearchParams(next);
  };

  const handleSortingChange = (sorting: SortingState) => {
    const next = new URLSearchParams(searchParams);
    const nextSort = sorting[0];
    next.delete("page");
    if (nextSort !== undefined) {
      next.set("orderBy", nextSort.id);
      next.set("order", nextSort.desc ? "desc" : "asc");
    } else {
      next.delete("orderBy");
      next.delete("order");
    }
    setSearchParams(next);
  };

  const handleColumnFiltersChange = (filters: ColumnFiltersState) => {
    const next = new URLSearchParams(searchParams);
    next.delete("page");
    const get = (id: string) => (filters.find((f) => f.id === id)?.value as string) ?? "";
    const deliveryId = get("deliveryId").trim();
    const vin = get("vin").trim();
    const vehicleCode = get("vehicleCode").trim();
    deliveryId ? next.set("deliveryId", deliveryId) : next.delete("deliveryId");
    vin ? next.set("vin", vin) : next.delete("vin");
    vehicleCode ? next.set("vehicleCode", vehicleCode) : next.delete("vehicleCode");
    setSearchParams(next);
  };

  const { table, setRowSelection } = useDataTable<Delivery>({
    data: data?.records ?? [],
    columns,
    pageCount,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    enableRowSelection,
    externalPagination: { pageIndex: page, pageSize },
    externalSorting: [{ id: orderBy, desc: order === "desc" }],
    externalColumnFilters,
    onPaginationChange: handlePaginationChange,
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: handleColumnFiltersChange,
    initialState: {
      columnVisibility: {
        deliveryId: false,
        vin: false,
        vehicleCode: false,
      },
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: clear selection when status tab or bulk-actions availability changes; `setRowSelection` alone would never re-run
  useEffect(() => {
    setRowSelection({});
  }, [statusKey.key, enableRowSelection, setRowSelection]);

  let activeFilterCount = 0;
  const textFilters = ["deliveryId", "vin"];
  const arrayFilters = [
    "deliveryType",
    "deliveryServices",
    "deliveryCountries",
    "organizationSlug",
  ];
  activeFilterCount += textFilters.filter((p) => searchParams.get(p)).length;
  activeFilterCount += arrayFilters.reduce((acc, p) => {
    const values = searchParams.get(p)?.split(",").filter(Boolean);
    return acc + (values?.length ?? 0);
  }, 0);
  if (searchParams.has("carDeliveryDeadlineFrom") || searchParams.has("carDeliveryDeadlineTo")) {
    activeFilterCount += 1;
  }

  return (
    <Box sx={{ p: 4 }}>
      <Stack gap={2}>
        <DataTable
          isLoading={isPending}
          table={table}
          totalCount={data?.total ?? 0}
          enableRowSelection={enableRowSelection}
          selectionBar={
            enableRowSelection ? (
              <DataTableSelectionBar table={table} entityLabel="delivery">
                <DeliveryTableBulkActions statusRoute={statusRoute} />
              </DataTableSelectionBar>
            ) : undefined
          }
        >
          <DataTableToolbar table={table}>
            {showMapButton && (
              <Button
                component={RouterLink}
                size="small"
                startIcon={<MapIcon />}
                to={mapRoute}
                variant="outlined"
              >
                Map
              </Button>
            )}
            <Badge badgeContent={activeFilterCount} color="primary">
              <Button
                onClick={() => setIsFiltersOpen((v) => !v)}
                size="small"
                startIcon={<FilterListIcon />}
                variant="outlined"
              >
                Filters
              </Button>
            </Badge>
            {canExport && (
              <Button
                onClick={() => setIsExportOpen(true)}
                size="small"
                startIcon={<DownloadIcon fontSize="small" />}
                variant="outlined"
              >
                Export
              </Button>
            )}
          </DataTableToolbar>
        </DataTable>

        {isError && (
          <Typography color="error" variant="body2">
            Failed to load deliveries.
          </Typography>
        )}
      </Stack>

      <Modal open={isFiltersOpen} onClose={() => setIsFiltersOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "calc(100% - 32px)",
            maxWidth: 960,
          }}
        >
          <Paper sx={{ p: 3 }}>
            <DeliveryTableFilters handleExpand={() => setIsFiltersOpen(false)} />
          </Paper>
        </Box>
      </Modal>

      <Dialog open={isExportOpen} onClose={() => setIsExportOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Export deliveries
          <IconButton
            aria-label="close"
            onClick={() => setIsExportOpen(false)}
            sx={{ color: theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <ExportFilterForm onCancel={() => setIsExportOpen(false)} userRole={userRole} />
        </DialogContent>
      </Dialog>
    </Box>
  );
}
